import { projectsApi, organizationApi } from '../../../services/api';
import OrgUnitTypeSelector from '../../common/OrgUnitTypeSelector';
import BillingTypeSelector from '../../common/BillingTypeSelector';
import ProjectPurposeSelector from '../../common/ProjectPurposeSelector';
import ProjectScopeTypeSelector from '../../common/ProjectScopeTypeSelector';
import { 
  ORG_UNIT_TYPE_RETURN_TYPES, 
  PROJECT_PURPOSE_RETURN_TYPES, 
  BILLING_TYPE_RETURN_TYPES,
  PROJECT_SCOPE_TYPE_RETURN_TYPES
} from '../../../constants/selectorReturnTypes';
import { getBillingName } from '../../../constants/billingTypes';
import { getScopeName, PROJECT_SCOPE_TYPES } from '../../../constants/projectScopeTypes';

const projectTypePolicyConfig = {
  entityName: 'Project Type Policy',
  entityNamePlural: 'Project Type Policies',
  
  // No required fields for this editable grid approach
  requiredFields: [],
  optionalFields: ['orgUnitTypeId', 'projectPurposeId', 'allowed', 'isDefault', 'defaultBillingType', 'defaultTimesheetRequired', 'defaultProjectScope'],
  
  // Field configurations
  fields: {
    orgUnitTypeId: {
      type: 'selector',
      label: 'Organizational Unit Type',
      component: OrgUnitTypeSelector,
      returnType: ORG_UNIT_TYPE_RETURN_TYPES.ID,
      required: false,
      formatDisplayValue: (value, fieldContext) => {
        if (!value) return 'Select...';
        const orgUnitType = fieldContext?.orgUnitTypes?.find(t => t.id == value); // Use == for type coercion
        return orgUnitType?.name || 'Unknown';
      }
    },
    projectPurposeId: {
      type: 'selector',
      label: 'Project Purpose',
      component: ProjectPurposeSelector,
      returnType: PROJECT_PURPOSE_RETURN_TYPES.ID,
      required: false,
      formatDisplayValue: (value, fieldContext) => {
        if (!value) return 'Select...';
        const purpose = fieldContext?.projectPurposes?.find(p => p.id == value); // Use == for type coercion
        return purpose?.name || 'Unknown';
      }
    },
    allowed: {
      type: 'boolean',
      label: 'Enabled',
      required: false,
      formatDisplayValue: (value) => value ? 'Yes' : 'No'
    },
    isDefault: {
      type: 'boolean',
      label: 'Default',
      required: false,
      formatDisplayValue: (value) => value ? 'Yes' : 'No'
    },
    defaultBillingType: {
      type: 'selector',
      label: 'Default Billing Type',
      component: BillingTypeSelector,
      returnType: BILLING_TYPE_RETURN_TYPES.ID,
      required: false,
      formatDisplayValue: (value) => {
        return getBillingName(value) || 'Not Set';
      },
      props: {
        showEmptyOption: false
      }
    },
    defaultTimesheetRequired: {
      type: 'boolean',
      label: 'Timesheet Required',
      required: false,
      formatDisplayValue: (value) => value ? 'Yes' : 'No'
    },
    defaultProjectScope: {
      type: 'selector',
      label: 'Default Project Scope',
      component: ProjectScopeTypeSelector,
      returnType: PROJECT_SCOPE_TYPE_RETURN_TYPES.ID,
      required: false,
      formatDisplayValue: (value) => {
        return getScopeName(value) || 'Not Set';
      },
      props: {
        showEmptyOption: false
      }
    }
  },
  
  // Table column order
  tableColumns: [
    { field: 'status', label: 'Status', width: 120 },
    { field: 'orgUnitTypeId', label: 'Organizational Unit Type', width: 200 },
    { field: 'projectPurposeId', label: 'Project Purpose', width: 200 },
    { field: 'allowed', label: 'Enabled', width: 100 },
    { field: 'isDefault', label: 'Default', width: 100 },
    { field: 'defaultBillingType', label: 'Default Billing Type', width: 180 },
    { field: 'defaultTimesheetRequired', label: 'Timesheet Required', width: 150 },
    { field: 'defaultProjectScope', label: 'Default Project Scope', width: 180 },
    { field: 'actions', label: 'Actions', width: 80 }
  ],
  
  // Status configurations
  statusConfig: {
    validationStatuses: {
      'valid': { label: 'Valid', color: '#10B981' },
      'invalid': { label: 'Incomplete', color: '#EF4444' },
      'pending': { label: 'Pending', color: '#6366F1' }
    }
  },
  
  // Documentation
  documentation: {
    description: 'Configure which organizational unit types are allowed to create projects for specific purposes. Each row represents a policy rule that defines the relationship between organizational unit types and project purposes.',
    skipFileUpload: true, // This tells ImportContainer to skip file upload UI
    isEditableGrid: true  // This tells ImportContainer this is an editable grid, not an import
  },
  
  // Import logic helpers
  helpers: {
    // Custom data loading
    loadData: async () => {
      try {
        const [policies, projectPurposes, orgUnitTypes] = await Promise.all([
          projectsApi.getAllProjectTypePolicies(),
          projectsApi.getProjectPurposes(),
          organizationApi.getOrgUnitTypes()
        ]);
        
        // Transform policies to match import format with validation status
        const transformedPolicies = policies.map((policy, index) => {
          const isValid = policy.orgUnitTypeId && policy.projectPurposeId;
          return {
            ...policy,
            id: policy.id || `policy_${index}`,
            validationStatus: isValid ? 'valid' : 'invalid',
            validationErrors: isValid ? [] : [
              { field: 'general', message: 'Both Organizational Unit Type and Project Purpose are required' }
            ],
            editable: true
          };
        });
        
        return {
          data: transformedPolicies,
          fieldContext: {
            projectPurposes,
            orgUnitTypes
          }
        };
      } catch (error) {
        throw new Error('Failed to load project type policies');
      }
    },

    // Custom save handler for individual cell changes
    saveItem: async (item, field, value) => {
      // Validation: Both orgUnitTypeId and projectPurposeId are required
      if (!item.orgUnitTypeId || !item.projectPurposeId) {
        throw new Error('Both Organizational Unit Type and Project Purpose are required');
      }

      if (item.id && item.id.startsWith('new_')) {
        // This is a new item - create it
        const newPolicy = await projectsApi.createProjectTypePolicy({
          orgUnitTypeId: item.orgUnitTypeId,
          projectPurposeId: item.projectPurposeId,
          allowed: item.allowed !== undefined ? item.allowed : true,
          isDefault: item.isDefault !== undefined ? item.isDefault : false,
          defaultBillingType: item.defaultBillingType || null,
          defaultTimesheetRequired: item.defaultTimesheetRequired !== undefined ? item.defaultTimesheetRequired : true,
          defaultProjectScope: item.defaultProjectScope || null
        });
        
        const updatedItem = { ...newPolicy, validationStatus: 'valid', editable: true };
        
        // If we're setting isDefault, we need to refresh all default values
        if (field === 'isDefault') {
          const allPolicies = await projectsApi.getAllProjectTypePolicies();
          return {
            updatedItem,
            shouldUpdateDefaults: true,
            allPolicies: allPolicies.map(p => ({ ...p, validationStatus: 'valid', editable: true }))
          };
        }
        
        return { updatedItem };
      } else {
        // Update existing item
        await projectsApi.updateProjectTypePolicy(item.id, { [field]: value });
        const updatedItem = { ...item, [field]: value };
        
        // If we're setting isDefault, we need to refresh all default values
        if (field === 'isDefault') {
          const allPolicies = await projectsApi.getAllProjectTypePolicies();
          return {
            updatedItem,
            shouldUpdateDefaults: true,
            allPolicies: allPolicies.map(p => ({ ...p, validationStatus: 'valid', editable: true }))
          };
        }
        
        return { updatedItem };
      }
    },

    // Add new row
    addNewItem: () => ({
      id: `new_${Date.now()}`,
      orgUnitTypeId: '',
      projectPurposeId: '',
      allowed: true,
      isDefault: false,
      defaultBillingType: null,
      defaultTimesheetRequired: true,
      defaultProjectScope: PROJECT_SCOPE_TYPES.GLOBAL,
      validationStatus: 'invalid',
      validationErrors: [
        { field: 'general', message: 'Both Organizational Unit Type and Project Purpose are required' }
      ],
      editable: true
    }),

    // Delete item
    deleteItem: async (item) => {
      if (item.id && !item.id.startsWith('new_')) {
        await projectsApi.deleteProjectTypePolicy(item.id);
      }
    },

    // Not used for editable grid
    getImportableItems: () => [],
    getStatusCounts: (items) => ({
      total: items.length,
      valid: items.length,
      invalid: 0,
      importable: 0
    })
  }
};

export default projectTypePolicyConfig;